import { CreditCard, DownloadSimple, FileText, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useGet } from "@/hooks/useResource";
import { Button } from "@/components/ui/button";
import { Loading, PageHeader, EmptyState } from "@/components/common";

const EVENTS_EMAIL = "events@gledswood.com.au";

const ICONS = { Compliance: ShieldCheck, Payment: CreditCard };

async function download(resource) {
  try {
    const response = await api.get(`/client/resources/${resource.id}/file`, {
      responseType: "blob",
    });
    const url = URL.createObjectURL(response.data);
    const link = document.createElement("a");
    link.href = url;
    link.download = resource.original_filename || `${resource.title}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  } catch {
    toast.error("This form is not available to download right now.");
  }
}

export default function Forms() {
  const { data, loading } = useGet("/client/resources");
  const forms = (data || []).filter((item) => item.category === "form");

  if (loading) {
    return <Loading label="Loading forms" />;
  }

  return (
    <div data-testid="forms-page">
      <PageHeader
        eyebrow="Planning"
        title="Forms"
        description={
          <>
            Download, sign and return the forms below. Please email signed copies back to{" "}
            <a
              href={`mailto:${EVENTS_EMAIL}?subject=Signed%20Gledswood%20form`}
              className="text-secondary underline-offset-4 hover:text-primary hover:underline"
              data-testid="forms-events-email"
            >
              {EVENTS_EMAIL}
            </a>{" "}
            — or hand them in at your next planning meeting.
          </>
        }
      />
      {forms.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="No forms published yet"
          description={`Your Gledswood Events Team publishes forms here. Email ${EVENTS_EMAIL} if you need one urgently.`}
        />
      ) : (
        <section className="grid grid-cols-1 gap-4 md:grid-cols-2" data-testid="form-cards">
          {forms.map((form) => (
            <FormCard key={form.id} form={form} />
          ))}
        </section>
      )}
    </div>
  );
}

function FormCard({ form }) {
  const Icon = ICONS[form.form_category] || FileText;
  return (
    <article
      className="flex flex-col rounded-lg border border-border bg-card p-6"
      data-testid={`form-card-${form.id}`}
    >
      <div className="flex items-start gap-4">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-accent/10 text-accent">
          <Icon size={22} weight="light" />
        </span>
        <div className="min-w-0 flex-1">
          {form.form_category && <p className="eyebrow">{form.form_category}</p>}
          <h2 className="font-heading text-2xl text-primary mt-1 break-words">{form.title}</h2>
        </div>
        {form.sign_required && (
          <span
            className="shrink-0 rounded-full border border-accent/40 bg-accent/10 px-3 py-1 text-xs uppercase tracking-wide text-accent"
            data-testid={`form-sign-required-${form.id}`}
          >
            Sign
          </span>
        )}
      </div>
      {form.description && (
        <p className="mt-4 text-sm text-muted-foreground">{form.description}</p>
      )}
      <p className="mt-4 flex items-center gap-2 border-t border-border pt-4 text-xs text-muted-foreground">
        <FileText size={15} />
        <span className="truncate">
          {String(form.mime || "").includes("pdf") ? "PDF" : "File"} ·{" "}
          {form.original_filename || form.title}
        </span>
      </p>
      <Button
        className="mt-4 w-full rounded-full bg-primary hover:bg-primary/90"
        onClick={() => download(form)}
        data-testid={`download-form-${form.id}`}
      >
        <DownloadSimple size={16} className="mr-1" />
        {form.sign_required ? "Download & Sign" : "Download"}
      </Button>
    </article>
  );
}
